<script setup lang="ts">
import { DEFAULT_LLM, DEFAULT_MODELS } from '@/utils/constants'
import { ref, watch, onMounted, computed, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
marked.setOptions({ breaks: true, gfm: true, silent: true })
import { useLocationTranslation } from '@/composables/useLocationTranslation'
import { useSpeech } from '@/composables/useSpeech'
import {
  iconGridLayout,
  formatTimeStandard,
  isTimeInPast
} from '../utils/weatherIcons'
import { isEventViewMode, getEventTypeShortName } from '../utils/eventTypes'
import { formatCsvForTable, displayValue, clearCsvCache } from '../utils/csvFormatting'
import { useTimezone, formatDateForAPI } from '@/composables/useTimezone'
import { useWeatherData, DAY_KEYS, VALID_VIEW_MODES, type ForecastData } from '@/composables/useWeatherData'
import { configService } from '../utils/configService'
import { useTouchHandling } from '@/composables/useTouchHandling'
import { useIconGrid, getWeatherIconDescription, getIconDataForTime as getIconDataForTimeRaw } from '@/composables/useIconGrid'
import ForecastChart from './ForecastChart.vue'
import WebcamPanel from './WebcamPanel.vue'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { useNavigationStore } from '@/stores/useNavigationStore'

const MAIN_SUMMARY_LENGTH = 320

const isValidViewMode = (mode: string): boolean => VALID_VIEW_MODES.includes(mode as any)

const { t: $t } = useI18n()
const settings = useSettingsStore()
const navStore = useNavigationStore()

const clampDayIndex = (day: number) =>
  Number.isFinite(day) ? Math.max(0, Math.min(Math.round(day), DAY_KEYS.length - 1)) : 0


const getDataByDayIndex = (index: number): ForecastData => forecastsArray.value[index]


const formatWeatherModels = (modelString: string): string => {
  if (!modelString) return 'Unknown'
  return modelString.split(',').map(m => configService.getWeatherModelShortName(m.trim())).join(', ')
}

const formatLLMName = (llm: string): string => configService.getLLMDisplayName(llm)

// State
const activeTab = ref(0)

const setActiveDay = (day: number) => {
  if (typeof day !== 'number' || Number.isNaN(day)) return
  const normalized = clampDayIndex(day)
  if (normalized !== activeTab.value) {
    activeTab.value = normalized
  } else {
    displayContent()
  }
}

// Respond to navigation store day requests
watch(() => navStore.requestedDay, (day) => {
  if (day !== undefined) {
    console.log(`📱 Forecast: Updating activeTab to ${day} (from navStore)`)
    setActiveDay(day)
  }
}, { immediate: true })

const viewMode = ref<string>('main')

// Timezone management
const {
  loadTimezoneFromStorage,
  watchTimezoneChanges,
  unwatchTimezoneChanges,
  updateTimezone,
  locationDates,
  formattedDayLabels
} = useTimezone(() => settings.language)

// Translated location names (city and country)
const { translatedCityName, translatedCountryName } = useLocationTranslation(
  () => settings.location,
  () => settings.language,
  () => settings.countryCode
)

// Speech synthesis
const { isReading, readContent } = useSpeech(
  () => isEventViewMode(viewMode.value) ? getEventsContent.value : forecastsArray.value[activeTab.value]?.text || '',
  () => settings.language
)

// Weather data fetching and state management
const {
  forecastsArray,
  currentEvents,
  currentEventType,
  isEventsLoading,
  eventsError,
  isWeeklyTextInFlight,
  displayContent,
  clearAllForecasts,
  setAllForecastsLoading,
  fetchWeeklyForecast
} = useWeatherData({
  getLocation: () => settings.location,
  getLanguage: () => settings.language,
  getCountryCode: () => settings.countryCode,
  getModelString: () => settings.modelString,
  getSelectedLLM: () => settings.selectedLLM,
  getTextLength: () => settings.textLength,
  // Fixed length for the main-view summary. The user's textLength setting
  // still controls the full text-view length; main view stays consistent
  // across users by always using MAIN_SUMMARY_LENGTH (#31 design).
  getSummaryLength: () => MAIN_SUMMARY_LENGTH,
  getSelectedAudience: () => settings.selectedAudience,
  getViewMode: () => viewMode.value,
  getActiveTab: () => activeTab.value,
  onTimezoneUpdate: updateTimezone,
  formatLLMName,
  formatWeatherModels
})

const goToMainView = () => { viewMode.value = 'main' }
const goToTableView = () => { viewMode.value = 'table' }
const goToIconView = () => { viewMode.value = 'icon' }


const safeUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined
  try {
    const parsed = new URL(url)
    return (parsed.protocol === 'https:' || parsed.protocol === 'http:') ? url : undefined
  } catch {
    return undefined
  }
}

const generateGoogleSearchUrl = (eventDescription: string): string => {
  const clean = (eventDescription || '')
    .replace(/\*\*/g, '')
    .replace(/[_`~#>]/g, '')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
  return `https://www.google.com/search?q=${encodeURIComponent(`${clean} ${settings.location} ${settings.countryCode || ''}`.trim())}`
}


// Clear forecasts and re-fetch all 7 days when settings change
watch([() => settings.language, () => settings.textLength, () => settings.selectedAudience], () => {
  clearAllForecasts()
  fetchWeeklyForecast()
})

// Refresh attribution on all forecasts that have text
const refreshForecastAttribution = () => {
  if (!configService.getConfig()) return
  DAY_KEYS.forEach((_key, index) => {
    const forecast = forecastsArray.value[index]
    if (forecast && forecast.text) {
      forecast.attribution = {
        llm: formatLLMName(settings.selectedLLM || DEFAULT_LLM),
        weatherModels: formatWeatherModels(settings.modelString || DEFAULT_MODELS)
      }
    }
  })
}

const clearWeatherCache = () => {
  try {
    Object.keys(localStorage).forEach(key => {
      // wital_token holds the auth JWT — it starts with `wital_` but is not
      // a weather cache entry. Do not wipe it, or the user silently loses
      // their session on next app start (issue surfaced during Phase 3
      // TestFlight device testing).
      if (key === 'wital_token') return
      if (key.includes('weather_') || key.startsWith('wital_')) localStorage.removeItem(key)
    })
    console.log('🧹 Cleared cached weather entries due to model/LLM change')
  } catch {}
}

watch([() => settings.modelString, () => settings.selectedLLM], () => {
  clearWeatherCache()
  clearAllForecasts()
  fetchWeeklyForecast()
})

watch(activeTab, displayContent)

watch(() => settings.location, () => {
  loadTimezoneFromStorage()
  clearAllForecasts()
  if (navStore.requestedDay !== undefined) {
    activeTab.value = navStore.requestedDay
  }
  fetchWeeklyForecast()
})

// Respond to nav store reload requests
watch(() => navStore.reloadTrigger, (trigger) => { if (trigger > 0) displayContent() })

// Respond to nav store view mode requests
// Watch the bumping nonce rather than requestedViewMode itself — that way
// the header logo can request 'main' repeatedly and each tap re-fires the
// watcher (Vue skips same-value writes on refs). #31.
watch(() => navStore.viewModeNonce, () => {
  const mode = navStore.requestedViewMode
  if (mode) setViewMode(mode)
})

// Lifecycle
onMounted(() => {
  // Default to day 0 if no navigation request was made
  if (navStore.requestedDay === undefined) {
    activeTab.value = 0
  }

  // Load timezone information from localStorage on mount
  loadTimezoneFromStorage()

  // Start watching for timezone changes
  watchTimezoneChanges()

  // Fetch all 7 days in one request (per-day fallback on tab switch if this fails).
  // Skip when no location is set yet — the auto-detect (#85) populates
  // settings.location asynchronously, and the watch above re-runs the fetch
  // once it lands. Without this guard the request goes out with location=""
  // and the UI sticks on "Loading weather…".
  if (settings.location && settings.location.trim()) fetchWeeklyForecast()

  // Listen for city selection started to show loading indicators immediately
  window.addEventListener('citySelectionStarted', handleCitySelectionStarted as EventListener)
  
  // Listen for settings changes to show loading indicators
  window.addEventListener('settingsChanged', handleSettingsChanged as EventListener)
  
  // Load backend config for LLM and model display names
  configService.loadConfig().then(() => {
    console.log('✅ Config loaded, refreshing attribution with correct short names')
    // Re-generate attribution with proper short names now that config is loaded
    refreshForecastAttribution()
  }).catch(error => {
    console.warn('Forecast: Failed to load backend config:', error)
    // Still try to update attribution with fallbacks
    setTimeout(() => {
      refreshForecastAttribution()
    }, 100)
  })
  
  // Update attribution for any existing forecasts that might be missing it
  setTimeout(() => {
    refreshForecastAttribution()
  }, 100)
})

onUnmounted(() => {
  clearCsvCache()

  // Clean up event listeners
  window.removeEventListener('citySelectionStarted', handleCitySelectionStarted as EventListener)
  window.removeEventListener('settingsChanged', handleSettingsChanged as EventListener)
  unwatchTimezoneChanges()
})

const getActiveContent = () =>
  isEventViewMode(viewMode.value)
    ? getEventsContent.value
    : getDataByDayIndex(activeTab.value).text

const copyToClipboard = async () => {
  const content = getActiveContent()
  if (!content) return
  try {
    const { copyText } = await import('../utils/clipboard')
    await copyText(content)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const shareContent = async () => {
  const content = getActiveContent()
  if (!content) return
  const dayName = $t(`forecast.dayNames.${DAY_KEYS[activeTab.value]}`)
  const shareTitle = `Weather forecast for ${settings.location} (${dayName})`
  const shareText = `${shareTitle}\n\n${content}\n\nFrom https://wital.ai`
  try {
    const { shareContent: doShare } = await import('../utils/share')
    await doShare({ title: shareTitle, text: shareText })
  } catch (err) {
    console.error('Error sharing:', err)
    try {
      const { copyText } = await import('../utils/clipboard')
      await copyText(shareText)
    } catch {}
  }
}

// Font size control functions
const decreaseFontSize = () => {
  const newSize = Math.max(1.0, settings.forecastFontSize - 0.1)
  if (newSize !== settings.forecastFontSize) settings.forecastFontSize = newSize
}

const increaseFontSize = () => {
  const newSize = Math.min(1.2, settings.forecastFontSize + 0.1)
  if (newSize !== settings.forecastFontSize) settings.forecastFontSize = newSize
}

const canDecreaseFontSize = computed(() => settings.forecastFontSize > 1.0)
const canIncreaseFontSize = computed(() => settings.forecastFontSize < 1.2)

// Text length control functions
const lengthOptions = [160, 320, 480] // Short, Medium, Long

const decreaseTextLength = () => {
  const currentIndex = lengthOptions.findIndex(len => len === settings.textLength)
  if (currentIndex > 0) settings.textLength = lengthOptions[currentIndex - 1]
}

const increaseTextLength = () => {
  const currentIndex = lengthOptions.findIndex(len => len === settings.textLength)
  if (currentIndex < lengthOptions.length - 1) settings.textLength = lengthOptions[currentIndex + 1]
}

const canDecreaseTextLength = computed(() => lengthOptions.findIndex(len => len === settings.textLength) > 0)
const canIncreaseTextLength = computed(() => lengthOptions.findIndex(len => len === settings.textLength) < lengthOptions.length - 1)


const clearEvents = () => {
  // Store the current event type before clearing
  if (isEventViewMode(viewMode.value) && currentEvents.value) {
    currentEventType.value = currentEvents.value.metadata?.event_type || null
  }
  
  currentEvents.value = null
  isEventsLoading.value = false
  eventsError.value = null
  // Don't reset viewMode here - let the calling function handle it
}

const generateEvents = (type: 'festivals' | 'lifestyle' | 'sports') => {
  viewMode.value = type
  displayContent()
}


// Computed to check if content is loaded (for AdSense compliance)
const hasContentLoaded = computed(() => {
  const data = forecastsArray.value[activeTab.value]
  if (!data) return false
  return !data.loading && !data.error && (!!data.text?.trim() || !!data.csvData?.trim())
})

const hasContentToCopy = computed(() => {
  if (isEventViewMode(viewMode.value)) {
    return !!currentEvents.value?.events?.length
  }
  const forecast = getDataByDayIndex(activeTab.value)
  return !!forecast.text?.trim() && !forecast.loading && !forecast.error
})

const getEventsContent = computed(() => {
  if (!currentEvents.value?.events) return ''
  const eventType = getEventTypeShortName(currentEvents.value.metadata?.event_type || 'Events')
  const location = currentEvents.value.metadata?.location || 'this location'
  const items = currentEvents.value.events.map((e: any, i: number) => `${i + 1}. ${e.description}`).join('\n')
  return `${eventType} in ${location}:\n\n${items}`.trim()
})

// Helper function to render markdown safely
const renderMarkdown = (text: string): string => {
  if (!text) return ''
  try {
    const result = marked(text)
    const html = typeof result === 'string' ? result : text
    return DOMPurify.sanitize(html)
  } catch (error) {
    console.error('Markdown rendering error:', error)
    return DOMPurify.sanitize(text.replace(/\n/g, '<br>'))
  }
}


// Check if location is available for event generation
const hasLocation = computed(() =>
  settings.location && settings.location.trim() && !settings.location.includes('Loading')
)


// Expose methods and computed properties to parent
// Method to set view mode from parent (e.g., for notification deep links)
const setViewMode = (mode: string) => {
  if (isValidViewMode(mode)) {
    viewMode.value = mode
  } else {
    console.warn(`📱 Forecast: Invalid view mode ${mode}, defaulting to main`)
    viewMode.value = 'main'
  }
}

defineExpose({ hasContentLoaded })


const { handleTouchStart, handleTouchMove, handleTouchEnd } = useTouchHandling({
  isLoading: () => forecastsArray.value.some(f => f.loading),
  onSwipeLeft: () => { if (activeTab.value < 6) activeTab.value++ },
  onSwipeRight: () => { if (activeTab.value > 0) activeTab.value-- }
})

watch(viewMode, (newMode, oldMode) => {
  if (!isValidViewMode(newMode)) {
    console.error(`❌ Invalid viewMode: "${newMode}", reverting to: "${oldMode || 'main'}"`)
    viewMode.value = oldMode || 'main'
  }
})


const formattedTableDataArray = computed(() =>
  forecastsArray.value.map(f => f.csvData ? formatCsvForTable(f.csvData) : [])
)

const { getIconData: getIconDataForTime, usedEmojisForDay } = useIconGrid({
  forecastsArray,
  formattedTableDataArray,
  locationDates,
  activeTab
})

const forecastTextStyle = computed(() => ({
  '--text-font-multiplier': settings.forecastFontSize
}))

// Coordinates from the first day's metadata. They're the same for
// every day of a given location, so we just read day 0 — once any
// forecast day has resolved, WebcamPanel can fetch a nearby webcam.
const webcamLat = computed<number | null>(() => {
  const v = forecastsArray.value[0]?.metadata?.latitude
  return typeof v === 'number' ? v : null
})
const webcamLon = computed<number | null>(() => {
  const v = forecastsArray.value[0]?.metadata?.longitude
  return typeof v === 'number' ? v : null
})


// Handle city selection started event to show loading indicators
const handleCitySelectionStarted = (event: CustomEvent) => {
  const { city } = event.detail
  console.log(`🔄 City selection started: ${city}`)
  
  const wasShowingEvents = isEventViewMode(viewMode.value)
  
  // Only set loading on weather days (not events)
  if (!wasShowingEvents) {
    setAllForecastsLoading()
  }
  
  // Clear events state when city changes to prevent showing old events
  currentEvents.value = null
  eventsError.value = null
  isEventsLoading.value = false
  
  if (wasShowingEvents) {
    // Longer delay to ensure location props are fully updated
    setTimeout(() => {
      displayContent()
    }, 200)
  }
  // Non-event views (main, text, table, icon) keep whatever view
  // the user picked. Previously we forced viewMode = 'text' here,
  // which meant a location change from the main combined view
  // punted the user into plain text — unexpected when most users
  // live on the default main view.
}

const handleSettingsChanged = (event: CustomEvent) => {
  const { type, value } = event.detail
  console.log(`⚙️ Settings changed: ${type} = ${value}`)
  setAllForecastsLoading()
}

const tableLegendContent = computed(() => [
  `🌡️ ${$t('forecast.tableRemarks.temperature')} | 💦 ${$t('forecast.tableRemarks.humidity')}`,
  `🍃 ${$t('forecast.tableRemarks.wind')}`,
  `☁️ ${$t('forecast.tableRemarks.cloudDefault')}`,
  `🌧️ ${$t('forecast.tableRemarks.rain')}`
])

// Shared helper for location metadata lines (used by table, icon metadata, icon info)
const getTimezoneOffset = (tz: string): string => {
  try {
    const now = new Date()
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000)
    const targetTimeStr = now.toLocaleString('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
    const match = targetTimeStr.match(/GMT([+-]\d{1,2}(?::\d{2})?)/)
    if (match) return match[1]
    const targetTime = new Date(now.toLocaleString('en-US', { timeZone: tz }))
    const offsetHours = Math.round((targetTime.getTime() - utcTime) / 3600000)
    return offsetHours >= 0 ? `+${offsetHours}` : `${offsetHours}`
  } catch {
    return '+0'
  }
}

interface ForecastMetaItem { label: string; value: string }

const buildAboutForecastItems = (): ForecastMetaItem[] => {
  const dateStr = formatDateForAPI(locationDates.value[activeTab.value])
  const metadata = forecastsArray.value[activeTab.value].metadata
  const countryCode = settings.countryCode || ''

  const timezone = metadata?.timezone || localStorage.getItem('locationTimezone') || 'UTC'
  const cityName = metadata?.city_name || settings.location
  const cityCountryCode = metadata?.country_code || countryCode
  const sunrise = metadata?.sunrise || ''
  const sunset = metadata?.sunset || ''

  let latitude = '0.0N'
  let longitude = '0.0E'
  if (metadata?.latitude !== undefined && metadata?.longitude !== undefined) {
    latitude = `${Math.abs(metadata.latitude).toFixed(1)}${metadata.latitude >= 0 ? 'N' : 'S'}`
    longitude = `${Math.abs(metadata.longitude).toFixed(1)}${metadata.longitude >= 0 ? 'E' : 'W'}`
  }
  if (latitude === '0.0N' && longitude === '0.0E') { latitude = 'N/A'; longitude = 'N/A' }

  const displayCityName = translatedCityName.value || cityName
  const displayCountryName = translatedCountryName.value || localStorage.getItem('cityCountryName') || cityCountryCode
  const timezoneDisplay = `UTC${getTimezoneOffset(timezone)}`
  const modelsUsed = formatWeatherModels(settings.modelString || 'Unknown')
  const textModelUsed = formatLLMName(settings.selectedLLM || DEFAULT_LLM)

  const items: ForecastMetaItem[] = [
    { label: $t('forecast.tableRemarks.date'), value: `${dateStr} (${timezoneDisplay})` }
  ]
  if (sunrise && sunset) {
    items.push({ label: $t('forecast.tableRemarks.daytime'), value: `${sunrise} – ${sunset}` })
  }
  items.push({
    label: $t('forecast.tableRemarks.location'),
    value: `${displayCityName || 'Unknown'}, ${displayCountryName || 'Unknown'} (${latitude}, ${longitude})`
  })
  items.push({ label: $t('forecast.tableRemarks.models'), value: modelsUsed })
  items.push({ label: $t('forecast.tableRemarks.textModel'), value: textModelUsed })
  return items
}

const aboutForecastItems = computed(buildAboutForecastItems)
const showAboutDialog = ref(false)


const isRowInPast = (timeStr: string, tabIndex: number): boolean =>
  tabIndex === 0 && isTimeInPast(timeStr, forecastsArray.value[0]?.metadata?.timezone || 'UTC')

const forecastDays = computed(() =>
  DAY_KEYS.map((key, index) => ({ key, index, value: index, formatted: formattedDayLabels.value[index] }))
)

const isIconViewAvailable = computed(() =>
  ['ecmwf-aifs', 'ncep-gfs', 'dwd-icon', 'ensemble'].some(s => (settings.modelString || '').includes(s))
)

const isChartAvailable = computed(() =>
  (formattedTableDataArray.value[activeTab.value] || []).length > 0
)

const eventMenuIcon = computed(() => {
  if (viewMode.value === 'festivals') return 'mdi-calendar-star'
  if (viewMode.value === 'lifestyle') return 'mdi-palette'
  if (viewMode.value === 'sports') return 'mdi-bike'
  return 'mdi-calendar-text'
})

const makeChartGetIcon = (dayIndex: number) => (timeStr: string) => getIconDataForTimeRaw(
  timeStr,
  dayIndex,
  formattedTableDataArray.value[dayIndex] || [],
  forecastsArray.value,
  locationDates.value
)

</script>

<template>
  <v-card variant="flat" class="forecast-card" @touchstart="handleTouchStart" @touchend="handleTouchEnd" @touchmove="handleTouchMove">
    <!-- Top button row (4-icon view selector + events overflow) removed in
         #31 — the main view replaces the view selector with tap-to-deep-dive
         regions, and Events is no longer user-reachable (see #28 / #30).
         The per-detail-view chevron-back was dropped too: tapping the header
         logo/wordmark returns to main (AppHeader → navStore.setViewMode). -->


    <v-tabs
      v-model="activeTab" 
      class="forecast-tabs"
      :show-arrows="false"
      density="compact"

    >
      <v-tab v-for="day in forecastDays" :key="day.key" :value="day.value">{{ day.formatted }}</v-tab>
    </v-tabs>
    
    <!-- Forecast content area -->
    <div class="forecast-content">
      <v-window v-model="activeTab" class="forecast-window">
                 <v-window-item v-for="day in forecastDays" :key="day.key" :value="day.value">
            <v-card-text>
              <div v-if="getDataByDayIndex(day.index).loading" class="events-loading">
                <v-progress-circular indeterminate></v-progress-circular>
                <div>{{ $t('forecast.loading.weather') }}</div>
              </div>
              <v-alert v-else-if="getDataByDayIndex(day.index).error" type="error">{{ getDataByDayIndex(day.index).error }}</v-alert>
              <template v-else>
                <!-- Events View -->
                <div v-if="isEventViewMode(viewMode)" class="events-view">
                  <div v-if="isEventsLoading" class="events-loading">
                    <v-progress-circular indeterminate></v-progress-circular>
                    <div>{{ $t('forecast.loading.' + viewMode) }}</div>
                  </div>
                  
                  <div v-else-if="eventsError" class="events-error">{{ eventsError }}</div>
                  
                  <div v-else-if="currentEvents && currentEvents.events.length > 0" class="events-content">
                    <div v-if="currentEvents.introductory_text" class="events-intro">
                      {{ currentEvents.introductory_text }}
                    </div>
                    <div class="events-container">
                      <ul class="events-list">
                        <li v-for="(event, index) in currentEvents.events.slice(0, 4)" :key="index"
                            class="event-item clickable">
                          <a :href="safeUrl(event.url) || generateGoogleSearchUrl(event.description)"
                             target="_blank" rel="noopener noreferrer"
                             class="event-link-full"
                             v-html="renderMarkdown(event.description)"></a>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  <div v-else class="events-empty">
                    {{ $t('forecast.noEventsFound.' + viewMode) }}
                  </div>
                </div>
                
                <!-- Main (combined) View — default landing for #31. Text summary
                     at a fixed length + time-series chart, each tappable for
                     deep-dive. Upper chart zone → table view, lower zone (icon
                     row / wind / beaufort) → icon view. -->
                <div v-else-if="viewMode === 'main'" class="forecast-main" :style="forecastTextStyle">
                  <div class="forecast-main-chart-wrap">
                    <ForecastChart
                      :table-data="formattedTableDataArray[day.index]"
                      :get-icon="makeChartGetIcon(day.index)"
                      :is-past="(t) => isRowInPast(t, day.index)"
                      compact
                    />
                    <!-- Transparent tap overlays split the chart into two
                         zones. Upper 70% covers legend + temperature/humidity
                         lines; lower 30% covers the icon row + wind caption
                         (SVG bottom margin is ~29% of the viewBox). -->
                    <button
                      class="forecast-main-chart-tap forecast-main-chart-tap--line"
                      type="button"
                      :aria-label="$t('forecast.openTableView')"
                      @click="goToTableView"
                    ></button>
                    <button
                      class="forecast-main-chart-tap forecast-main-chart-tap--icon"
                      type="button"
                      :aria-label="$t('forecast.openIconView')"
                      @click="goToIconView"
                    ></button>
                  </div>

                  <div class="forecast-main-footer">
                    <span class="forecast-footer-label">{{ $t('forecast.chartLegend.wind') }}</span>
                    <span class="forecast-footer-label">{{ $t('forecast.tapSectionsForDetails') }}</span>
                    <a class="about-forecast-link" @click="showAboutDialog = true">
                      {{ $t('forecast.aboutShort') }}
                    </a>
                  </div>

                  <!-- Raised-divider panel wrapping both the forecast text
                       (scrolls internally) and the action row so they read
                       as one continuous floating surface instead of a text
                       block + detached chip row. -->
                  <div class="forecast-main-panel">
                    <div v-if="getDataByDayIndex(day.index).text" class="forecast-action-buttons">
                    <v-btn
                      class="forecast-action-button"
                      @click="readContent"
                      :disabled="!hasContentToCopy"
                      :title="isReading ? $t('forecast.stopReading') : (hasContentToCopy ? $t('forecast.readForecastAloud') : $t('forecast.loadingForecast'))"
                      variant="text"
                      size="small"
                    >
                      <div class="forecast-action-button-content">
                        <v-icon>{{ isReading ? 'mdi-stop' : 'mdi-volume-high' }}</v-icon>
                      </div>
                    </v-btn>
                    <v-btn
                      class="forecast-action-button"
                      @click="copyToClipboard"
                      :disabled="!hasContentToCopy"
                      :title="hasContentToCopy ? $t('forecast.copyForecast') : $t('forecast.loadingForecast')"
                      variant="text"
                      size="small"
                    >
                      <div class="forecast-action-button-content">
                        <v-icon>mdi-content-copy</v-icon>
                      </div>
                    </v-btn>
                    <v-btn
                      class="forecast-action-button"
                      @click="shareContent"
                      :disabled="!hasContentToCopy"
                      :title="hasContentToCopy ? $t('forecast.shareForecast') : $t('forecast.loadingForecast')"
                      variant="text"
                      size="small"
                    >
                      <div class="forecast-action-button-content">
                        <v-icon>mdi-share-variant</v-icon>
                      </div>
                    </v-btn>
                    <v-btn
                      class="forecast-action-button"
                      @click="decreaseFontSize"
                      :disabled="!canDecreaseFontSize"
                      :title="$t('forecast.decreaseFontSize') || 'Decrease font size'"
                      variant="text"
                      size="small"
                    >
                      <div class="forecast-action-button-content">
                        <v-icon>mdi-magnify-minus</v-icon>
                      </div>
                    </v-btn>
                    <v-btn
                      class="forecast-action-button"
                      @click="increaseFontSize"
                      :disabled="!canIncreaseFontSize"
                      :title="$t('forecast.increaseFontSize') || 'Increase font size'"
                      variant="text"
                      size="small"
                    >
                      <div class="forecast-action-button-content">
                        <v-icon>mdi-magnify-plus</v-icon>
                      </div>
                    </v-btn>
                    <v-btn
                      class="forecast-action-button"
                      @click="decreaseTextLength"
                      :disabled="!canDecreaseTextLength"
                      :title="$t('forecast.decreaseTextLength') || 'Decrease text length'"
                      variant="text"
                      size="small"
                    >
                      <div class="forecast-action-button-content">
                        <svg viewBox="0 0 24 24" class="text-length-svg">
                          <path d="M2 5h20v2H2V5zm0 4h20v2H2V9z"/>
                        </svg>
                      </div>
                    </v-btn>
                    <v-btn
                      class="forecast-action-button"
                      @click="increaseTextLength"
                      :disabled="!canIncreaseTextLength"
                      :title="$t('forecast.increaseTextLength') || 'Increase text length'"
                      variant="text"
                      size="small"
                    >
                      <div class="forecast-action-button-content">
                        <svg viewBox="0 0 24 24" class="text-length-svg">
                          <path d="M2 5h20v2H2V5zm0 4h20v2H2v-2zm0 4h20v2H2v-2z"/>
                        </svg>
                      </div>
                    </v-btn>
                  </div>

                    <div class="forecast-main-text">
                      <div v-if="!getDataByDayIndex(day.index).text && isWeeklyTextInFlight" class="forecast-main-loading">
                        <v-progress-circular indeterminate size="20"></v-progress-circular>
                        <span>{{ $t('forecast.loading.weather') }}</span>
                      </div>
                      <div v-else class="forecast-main-text-content">{{ getDataByDayIndex(day.index).text }}</div>
                    </div>
                  </div>
                </div>

                <!-- Table View -->
                <div v-else-if="viewMode === 'table'" class="forecast-table">
                  <div v-if="formattedTableDataArray[day.index].length > 0">
                    <v-table theme="dark" class="weather-table">
                      <thead>
                        <tr>
                          <th>🕐</th>
                          <th>🌡️</th>
                          <th>💦</th>
                          <th>🍃</th>
                          <th>☁️</th>
                          <th>🌧️</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in formattedTableDataArray[day.index]" :key="row.time" 
                            :class="{ 'past-time-row': isRowInPast(row.time, day.index) }">
                          <td>{{ formatTimeStandard(row.time) }}</td>
                          <td>{{ displayValue(row.temp) }}</td>
                          <td>{{ displayValue(row.humidity) }}</td>
                          <td>{{ displayValue(row.wind) }}</td>
                          <td>{{ displayValue(row.cloud) }}</td>
                          <td>{{ displayValue(row.rain) }}</td>
                        </tr>
                      </tbody>
                    </v-table>
                    <!-- Table Legend (emoji explanations) -->
                    <div class="table-legend">
                      <div v-for="legend in tableLegendContent" :key="legend">{{ legend }}</div>
                    </div>
                    <!-- About this forecast (table view) -->
                    <div class="about-forecast-row">
                      <a class="about-forecast-link" @click="showAboutDialog = true">
                        {{ $t('forecast.aboutThisForecast') }}
                      </a>
                    </div>
                  </div>
                  <div v-else class="no-table-data">{{ $t('forecast.noTableData') }}</div>
                </div>
                
                <!-- Icon View -->
                <div v-else-if="viewMode === 'icon'" class="forecast-icon">
                   <div v-if="formattedTableDataArray[day.index].length > 0" class="icon-grid-container">
                    <div class="icon-grid fill-vertical">
                      <div v-for="(row, rowIndex) in iconGridLayout" :key="rowIndex" class="icon-row">
                        <div v-for="(timeStr, colIndex) in row" :key="`${rowIndex}-${colIndex}`" class="icon-cell"
                             :class="{ 'past-time-cell': isRowInPast(timeStr, day.index) }">
                          <div v-if="getIconDataForTime(timeStr, day.index).hasData" class="icon-data">
                            <div class="icon-temp-humidity">
                              <span>{{ getIconDataForTime(timeStr, day.index).temp }} {{ getIconDataForTime(timeStr, day.index).humidity }}</span>
                            </div>
                            <div class="weather-icon">
                              <img 
                                :src="`/icons/qweather/${getIconDataForTime(timeStr, day.index).icon}.svg`" 
                                :alt="`${getWeatherIconDescription(getIconDataForTime(timeStr, day.index).icon)} at ${getIconDataForTime(timeStr, day.index).timeLabel}`"
                                class="weather-icon-img"
                              />
                            </div>
                            <div class="time-label">{{ getIconDataForTime(timeStr, day.index).timeLabel }}</div>
                            <div class="weather-emoji">
                              {{ getIconDataForTime(timeStr, day.index).emoji || '⠀' }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Emoji Legend - Dynamic based on actual usage -->
                    <div class="emoji-legend">
                      <div class="emoji-legend-title">{{ $t('forecast.iconView.actionsWorthConsidering') }}</div>
                      <div class="emoji-legend-grid">
                        <div 
                          v-for="emojiItem in usedEmojisForDay" 
                          :key="emojiItem.key" 
                          class="emoji-legend-item"
                        >
                          {{ emojiItem.emoji }} {{ $t(`forecast.iconView.${emojiItem.key}`) }}
                        </div>
                      </div>
                    </div>
                    <!-- About this forecast (icon view) -->
                    <div class="about-forecast-row">
                      <a class="about-forecast-link" @click="showAboutDialog = true">
                        {{ $t('forecast.aboutThisForecast') }}
                      </a>
                    </div>
                  </div>
                  <div v-else class="no-icon-data">
                    <div>{{ $t('forecast.noIconData') }}</div>
                    <div class="no-icon-data-hint">
                      Icon view requires cloud cover data. Please select ECMWF-AIFS, NCEP-GFS, DWD/ICON, or ensemble models in Settings.
                    </div>
                    <v-btn
                      @click="goToMainView"
                      variant="outlined"
                      size="small"
                      class="mt-2"
                    >
                      Back to main view
                    </v-btn>
                  </div>
                </div>

              </template>
            </v-card-text>
        </v-window-item>
      </v-window>

      <!-- Nearest webcam panel (#51). Location-scoped, not day-scoped,
           so it sits outside the per-day v-window and renders once per
           resolved location. Gated to main view — the other view modes
           (text / table / icon) are information-dense enough that the
           webcam card pushes their primary content off-screen. Hides
           itself when no coverage. -->
      <WebcamPanel
        v-if="viewMode === 'main'"
        :latitude="webcamLat"
        :longitude="webcamLon"
      />
    </div>

    <!-- About this forecast dialog -->
    <v-dialog v-model="showAboutDialog" max-width="420">
      <v-card class="about-forecast-card">
        <v-card-title class="about-forecast-title">
          {{ $t('forecast.aboutThisForecast') }}
        </v-card-title>
        <v-card-text>
          <div class="about-forecast-grid">
            <template v-for="item in aboutForecastItems" :key="item.label">
              <div class="about-forecast-label">{{ item.label }}</div>
              <div class="about-forecast-value">{{ item.value }}</div>
            </template>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showAboutDialog = false">{{ $t('common.close') || 'Close' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<style scoped>
/* Override any default Vuetify card margins */
:deep(.v-card.forecast-card) {
  margin: 0;
}

.forecast-card {
  background-color: var(--bg-primary);
  border-radius: 0;
  /* Was `height: calc(100vh - 84px - safe-area-inset-bottom)` + `overflow: hidden`
   * so the card fit exactly one viewport and never scrolled. Now that the
   * webcam panel and other location content can push the column past one
   * viewport, use min-height instead and let the normal page scroll handle
   * overflow — the sticky AppHeader and fixed ChatBar stay put while the
   * middle scrolls. */
  min-height: calc(100vh - 84px - env(safe-area-inset-bottom));
  width: 100%;
  margin: 0;
  padding: 0;

  display: flex;
  flex-direction: column;
  touch-action: pan-x pan-y;
  -webkit-overflow-scrolling: touch;
  user-select: none; /* Prevent text selection during swipe */
}

.forecast-content {
  position: relative;
  /* overflow: visible so the WebcamPanel (mounted outside the
   * v-window but inside .forecast-content) can push the column
   * naturally instead of getting clipped. */
}

.v-card-text {
  flex: 1;
  overflow-y: auto;
  padding: 2px 6px 4px;
  width: 100%;
  margin: 0 !important;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x pan-y;
  text-align: left;
  background-color: var(--bg-primary);
}

.button-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background-color: var(--bg-primary);
}


.view-mode-buttons-left,
.event-buttons-top {
  display: flex;
  gap: 8px;
}

/* Global icon size override for all icons in forecast component */
.forecast-card .v-icon {
  font-size: calc(1.2rem * var(--base-font-multiplier, 1.0)) !important;
  width: calc(1.2rem * var(--base-font-multiplier, 1.0)) !important;
  height: calc(1.2rem * var(--base-font-multiplier, 1.0)) !important;
}

/* Shared base for all circular action buttons */
.event-button-top,
.action-button-top,
.forecast-action-button {
  background-color: var(--surface) !important;
  color: var(--text-primary) !important;
  backdrop-filter: blur(10px);
  border: none !important;
  border-radius: 50% !important;
  transition: all 0.3s ease;
  min-width: 44px !important;
  width: 44px !important;
  height: 44px !important;
  padding: 0 !important;
}

.event-button-top,
.action-button-top {
  z-index: 10;
}

/* Force consistent sizing and remove Vuetify button padding */
:deep(.event-button-top.v-btn),
:deep(.action-button-top.v-btn) {
  min-width: 44px !important;
  width: 44px !important;
  height: 44px !important;
  padding: 0 !important;
  margin: 0 !important;
}

:deep(.event-button-top .v-btn__content),
:deep(.action-button-top .v-btn__content) {
  padding: 0 !important;
  margin: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

.event-button-top:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.1);
}

.action-button-top:hover:not(:disabled),
.forecast-action-button:hover:not(:disabled) {
  background-color: var(--surface-hover) !important;
  transform: scale(1.1);
}

.event-button-top:disabled,
.action-button-top:disabled,
.forecast-action-button:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
}

.event-button-top:disabled:hover,
.action-button-top:disabled:hover,
.forecast-action-button:disabled:hover {
  transform: none !important;
}

.action-button-content,
.forecast-action-button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.view-button-active {
  background-color: var(--surface-hover) !important;
  color: var(--text-primary) !important;
}

/* Events Display Styles - Optimized for no-scroll layout */
.events-view,
.forecast-icon {
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.events-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 20px;
  color: var(--text-primary);
}

.events-error,
.events-empty {
  text-align: center;
  padding: 40px 20px;
  font-size: 1.1rem;
}

.events-error { color: #ff6b6b; }
.events-empty { color: var(--text-secondary); }

.events-content {
  padding: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.events-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  padding: 0 4px;
}


.events-list {
  list-style: none;
  padding: 0 16px;
  margin: 0;
}

.events-intro {
  padding: 8px 16px;
  margin-bottom: 8px;
  color: var(--text-primary);
  font-size: 0.95rem;
  line-height: 1.4;
  background-color: var(--surface);
  border-radius: 8px;
  border-left: 3px solid rgba(255, 255, 255, 0.2);
}

.event-item {
  padding: 2px 0;
  margin-bottom: 6px;
  color: var(--text-primary);
  font-size: 1.0rem;
  line-height: 1.2;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
}

.event-item:hover {
  background-color: var(--surface);
  border-radius: 4px;
}

.event-item.clickable {
  cursor: pointer;
}

.event-link-full {
  color: inherit;
  text-decoration: none;
  transition: all 0.2s ease;
}

.event-link-full:hover {
  color: var(--text-primary);
  background-color: var(--surface);
  border-radius: 4px;
  /* Avoid padding/margin tweaks to prevent layout shift on click */
  outline: 2px solid transparent;
}

.event-item:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

.event-item strong {
  font-weight: 600;
}

.event-item em {
  color: var(--text-secondary);
  font-style: italic;
}

.forecast-action-buttons {
  display: flex;
  gap: 8px;
  padding: 8px 14px 4px;
  justify-content: center;
  flex-shrink: 0;
}

.text-length-svg {
  width: calc(1.2rem * var(--base-font-multiplier, 1.0)) !important;
  height: calc(1.2rem * var(--base-font-multiplier, 1.0)) !important;
  fill: currentColor;
}

/* Restore 7-day tabs layout */
:deep(.v-tabs) {
  width: 100% !important;
  margin: 0;
  padding: 0;
  background-color: var(--bg-primary) !important;
}

:deep(.v-tab) {
  flex: 1 1 14.28% !important; /* 100% / 7 tabs = 14.28% */
  font-weight: 500 !important;
  text-transform: none !important;
  color: var(--text-secondary) !important;
  padding: 0 2px !important; /* Further reduced padding for 7 tabs */
  margin: 0 !important; /* Zero margin */
  min-width: 0 !important; /* Allow tabs to shrink */
  font-size: calc(0.875rem * var(--base-font-multiplier, 1.0)) !important; /* Adaptive font size */
}

:deep(.v-tab--selected) {
  color: var(--text-primary) !important;
}

.forecast-table {
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
}

.weather-table {
  background-color: transparent !important;
  color: var(--text-primary) !important;
  margin: 0 !important;
  padding: 0 !important;
}

:deep(.weather-table .v-table__wrapper) {
  background-color: transparent !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow-x: auto !important;
}

:deep(.weather-table th),
:deep(.weather-table td) {
  color: var(--text-primary) !important;
  border-bottom: 1px solid var(--border-light) !important;
  text-align: center !important;
  padding: 4px 2px !important;
  margin: 0 !important;
  white-space: nowrap !important;
  line-height: 1.2 !important;
  height: auto !important;
  min-height: 0 !important;
}

:deep(.weather-table th) {
  background-color: var(--surface) !important;
  font-weight: 600 !important;
}


:deep(.weather-table tr) {
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
  min-height: 0 !important;
  line-height: 1 !important;
}

:deep(.weather-table tr:hover) {
  background-color: var(--surface) !important;
}

:deep(.weather-table tbody),
:deep(.weather-table thead) {
  margin: 0 !important;
  padding: 0 !important;
}

/* Past time row styling - dimmer appearance */
:deep(.weather-table tr.past-time-row) {
  opacity: 0.7 !important;
  background-color: var(--surface) !important;
}

:deep(.weather-table tr.past-time-row td) {
  color: var(--text-secondary) !important;
}

:deep(.weather-table tr.past-time-row:hover) {
  background-color: var(--surface-hover) !important;
}

.icon-cell.past-time-cell .icon-data {
  opacity: 0.55;
}

.no-table-data,
.no-icon-data {
  text-align: center;
  color: var(--text-secondary);
  margin-top: 60px;
  font-size: 1.2rem;
}

/* Icon View Styles */
.icon-grid-container {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  height: 100%;
  width: 100%;
}

.icon-grid.fill-vertical {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 2px;
  background-color: var(--surface-hover);
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  min-height: 300px;
  max-width: 700px;
  margin: 0 auto;
}

.icon-row {
  display: contents;
}

.icon-cell {
  background-color: var(--bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  min-width: 100px;
  padding: 4px;
  height: 100%;
  width: 100%;
  position: relative;
}

.icon-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 1px;
  margin: 0;
  padding: 0;
}

.icon-temp-humidity {
  display: flex;
  flex-direction: row;
  gap: 4px;
  font-size: 0.9rem;
  color: var(--text-primary);
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
}

.weather-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14vw; /* Reduced from 16.67vw to leave more space for legends */
  height: 14vw;
  max-width: 100px; /* Reduced from 120px */
  max-height: 100px; /* Reduced from 120px */
  min-width: 50px; /* Reduced from 60px */
  min-height: 50px; /* Reduced from 60px */
  margin: 2px;
  padding: 2px;
}

.weather-icon-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  color: var(--text-primary);
}

/* Theme-specific icon filters */
:root .weather-icon-img {
  filter: brightness(0) invert(1); /* White icons in dark mode */
}

[data-theme="light"] .weather-icon-img {
  filter: brightness(0) invert(0.2); /* Dark gray icons in light mode */
}

.weather-emoji {
  font-size: 1.0rem;
  text-align: center;
  line-height: 1;
  margin: 2px;
  padding: 2px;
  min-height: 1em;
}

.emoji-legend {
  margin: 4px 0 0 0;
  padding: 4px 0;
  text-align: center;
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.6;
}

.emoji-legend-title {
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--text-primary);
}

.emoji-legend-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 8px;
  justify-items: start;
  text-align: left;
}

.emoji-legend-item {
  padding: 1px 2px;
  white-space: nowrap;
}

.time-label {
  font-size: 1.0rem;
  color: var(--text-secondary);
  text-align: center;
  font-weight: 500;
}


.no-icon-data-hint {
  margin-top: 1rem;
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.4;
}

.icon-grid.fill-vertical {
  min-height: 180px;
  max-width: 100%;
}
.icon-cell {
  min-height: 60px;
  min-width: 60px;
  padding: 4px;
}
.icon-temp-humidity {
  font-size: 0.9rem !important;
}
.weather-emoji,
.time-label {
  font-size: 1.0rem !important;
}
.weather-icon {
  max-width: 80px;
  max-height: 80px;
  min-width: 40px;
  min-height: 40px;
}
.emoji-legend,
.emoji-legend-title,
.emoji-legend-item {
  font-size: 0.9rem !important;
}
.about-forecast-link {
  font-size: calc(0.8rem * var(--base-font-multiplier, 1.0)) !important;
}
:deep(.weather-table th),
:deep(.weather-table td) {
  font-size: 1.0rem !important;
}

.table-legend {
  margin-top: 4px;
  padding: 4px;
  background-color: var(--bg-primary);
  border-radius: 8px;
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.3;
}

.table-legend div:not(:last-child) {
  margin-bottom: 2px;
}

.about-forecast-row {
  margin-top: 8px;
  padding: 4px 8px;
  text-align: center;
}

.about-forecast-link {
  color: var(--accent);
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.about-forecast-link:hover {
  opacity: 0.85;
}

.about-forecast-card {
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

.about-forecast-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.about-forecast-grid {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 12px;
  row-gap: 6px;
  font-size: 0.9rem;
  line-height: 1.3;
}

.about-forecast-label {
  font-weight: 600;
  color: var(--text-secondary);
  white-space: nowrap;
}

.about-forecast-value {
  color: var(--text-primary);
  word-break: break-word;
}

/* ── Main (combined) view — #31 ─────────────────────────────────────── */
.forecast-main {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 6px 0;
  /* Explicit viewport-math cap (#68). The flex chain from `#app` →
   * `.v-main` → `.v-container` → `.content-wrapper` → `.forecast-card`
   * → `.v-window` → `.v-window-item` → `.v-card-text` does not bound
   * us: `.content-wrapper` is `height: auto`, and `.v-window-item`
   * isn't a flex container so `.v-card-text: flex: 1` is inert. That
   * made the previous `height: 100%` resolve to `auto`, and
   * `.forecast-main-text`'s internal `overflow-y: auto` never fired.
   * Subtract the heights published at runtime by AppHeader
   * (--app-header-height), ChatBar (--chat-dock-height, already
   * includes its safe-area padding) and WebcamPanel
   * (--webcam-dock-height) from the dynamic viewport, plus the
   * compact v-tabs bar, so we're guaranteed a bounded box. */
  height: calc(
    100vh
    - var(--app-header-height, 84px)
    - 36px
    - var(--chat-dock-height, calc(56px + env(safe-area-inset-bottom)))
    - var(--webcam-dock-height, 0px)
  );
  height: calc(
    100dvh
    - var(--app-header-height, 84px)
    - 36px
    - var(--chat-dock-height, calc(56px + env(safe-area-inset-bottom)))
    - var(--webcam-dock-height, 0px)
  );
}

/* On main view only, drop .v-card-text's bottom padding so the
 * forecast text block can butt up against the webcam dock without a
 * sliver of bg showing through. Other views keep their padding-bottom
 * breathing room. */
.v-card-text:has(.forecast-main) {
  padding-bottom: 0 !important;
}

/* Raised-divider panel — hairline border-top + upward ambient shadow
   mirror the chat-dock's edge. Wraps the scrolling text body and the
   pinned action row so they read as one continuous floating surface,
   and extends horizontally to the column edges (negative margins escape
   both .forecast-main and .v-card-text horizontal padding). */
.forecast-main-panel {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin: 0 -12px;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.08);
}

.forecast-main-text {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* iOS momentum scrolling — modern WebKit defaults to it, but keeping
   * the explicit rule for older shells that regress without it. */
  -webkit-overflow-scrolling: touch;
  /* Don't chain scroll into .v-card-text / the outer page scroll when
   * the user hits the top/bottom of the text — the swipe handler on
   * .forecast-card would otherwise pick up the residual gesture and
   * misfire as a day-swap. */
  overscroll-behavior: contain;
  /* Explicit pan-y so iOS is certain this container handles vertical
   * gestures itself, not the swipe handler on .forecast-card. */
  touch-action: pan-y;
  padding: 4px 14px 12px;
  /* Tracks the action-row font-size buttons (--text-font-multiplier). */
  font-size: calc(0.9rem * var(--text-font-multiplier, 1));
  line-height: 1.35;
}

.forecast-main-text-content {
  white-space: pre-wrap;
}

.forecast-main-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(var(--v-theme-on-surface), 0.7);
  font-size: 0.9rem;
}

.forecast-main-chart-wrap {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
}

/* The chart itself is semi-interactive (Vuetify svg), so the tap zones
   sit on top as transparent buttons. Keep pointer-events on so clicks
   land on them rather than dots/paths inside the SVG. */
.forecast-main-chart-tap {
  position: absolute;
  left: 0;
  right: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.forecast-main-chart-tap:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: -2px;
}

/* Top zone covers the legend + temperature/humidity lines; bottom zone
   covers the icon row + wind caption. The SVG viewBox reserves ~29% of
   its height for the icon/wind area (M.bottom = 92 of 320). */
.forecast-main-chart-tap--line {
  top: 0;
  height: 70%;
}

.forecast-main-chart-tap--icon {
  bottom: 0;
  height: 30%;
}

/* 3-column grid so the middle "Tap sections for details" hint is
 * centred against the viewport, not just space-between of the other
 * two items. Left and right columns flex equal widths so the centre
 * stays anchored even if the labels on either side differ. */
.forecast-main-footer {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 4px 12px;
  padding: 0 4px;
}

.forecast-main-footer .about-forecast-link {
  font-size: 0.7rem !important;
  color: rgb(var(--v-theme-primary));
  white-space: nowrap;
  justify-self: end;
}

/* Matched typography for both left (Tap hint) and centre (Wind
 * legend) labels so they read as a balanced pair instead of the
 * previous mismatched pair. */
.forecast-footer-label {
  font-size: 0.7rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
  white-space: nowrap;
}

.forecast-footer-label:first-child {
  justify-self: start;
}
</style>
